import { useAuthStore } from '@/stores/authStore'
import { DeadlineWidget } from '@/components/Dashboard'
import { SupervisorStatsGrid } from '@/components/Dashboard/SupervisorStatsGrid'
import { NeedsAttention } from '@/components/Dashboard/NeedsAttention'
import { TeamWorkload } from '@/components/Dashboard/TeamWorkload'
import { ProactiveCard } from '@/components/AgentActivity/ProactiveCard'
import { useProactiveCards } from '@/hooks/useProactiveCards'
import { useTeamWorkload, useTeamTimeSummary } from '@/hooks/useTeamWorkload'
import { useDashboardStats } from '@/hooks'

function getGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

export function SupervisorDashboard() {
  const { user } = useAuthStore()
  const { suggestions, dismiss } = useProactiveCards()
  const { data: workloadData, isLoading: workloadLoading } = useTeamWorkload()
  const { data: timeSummary, isLoading: timeLoading } = useTeamTimeSummary()
  const { stats, isLoading: statsLoading } = useDashboardStats()

  const firstName = user?.name?.split(' ')[0] || 'there'

  // Compute supervisor-specific stats
  const firmActiveTasks = workloadData?.total_active ?? 0
  const teamHoursThisWeek = timeSummary?.total_hours ?? 0
  const overdueTasks = stats.overdueTasks
  const upcomingDeadlines = stats.upcomingDeadlines

  const isLoading = workloadLoading || timeLoading || statsLoading

  return (
    <div className="space-y-6">
      {/* Proactive agent suggestions */}
      {suggestions.length > 0 && (
        <div className="space-y-2">
          {suggestions.map((s) => (
            <ProactiveCard key={s.id} suggestion={s} onDismiss={dismiss} />
          ))}
        </div>
      )}

      {/* Welcome message */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {getGreeting()}, {firstName}
        </h1>
        <p className="text-muted-foreground">
          Here's your firm's status today.
        </p>
      </div>

      {/* Supervisor stats grid */}
      <SupervisorStatsGrid
        firmActiveTasks={firmActiveTasks}
        teamHoursThisWeek={teamHoursThisWeek}
        overdueTasks={overdueTasks}
        upcomingDeadlines={upcomingDeadlines}
        isLoading={isLoading}
      />

      {/* Main content grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Left column - Needs Attention */}
        <NeedsAttention />

        {/* Right column - Team Workload & Deadlines */}
        <div className="space-y-6">
          <TeamWorkload />
          <DeadlineWidget />
        </div>
      </div>
    </div>
  )
}
